import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Account from './Account';
import Subscriptions from './Subscriptions';
import './Settings.css';

const Settings = () => {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState('account');
    const [statusMessage, setStatusMessage] = useState({ type: '', text: '' });

    // --- Tier & Integration States ---
    const [userTier, setUserTier] = useState('free');
    const [loadingTier, setLoadingTier] = useState(true);
    const [isHubspotConnected, setIsHubspotConnected] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    // Fetch user profile tier & HubSpot status on mount
    useEffect(() => {
        const fetchInitialData = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                setLoadingTier(false);
                return;
            }

            const authHeader = { headers: { Authorization: `Bearer ${token}` } };

            try {
                // Fetch User Profile
                const profileRes = await axios.get(`${API_URL}/api/users/profile`, authHeader);
                setUserTier(profileRes.data.tier || 'free');

                // Fetch Integration Status
                const statusRes = await axios.get(`${API_URL}/api/auth/hubspot/status`, authHeader);
                setIsHubspotConnected(statusRes.data.isConnected);
            } catch (error) {
                console.error('Error loading settings data:', error);
                setUserTier('free');
            } finally {
                setLoadingTier(false);
            }
        };

        fetchInitialData();
    }, [API_URL]);

    const cleanUpQueryParams = useCallback(() => {
        searchParams.delete('integration');
        searchParams.delete('error');
        setSearchParams(searchParams);
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (tab) setActiveTab(tab);

        const integrationStatus = params.get('integration');
        const authError = params.get('error');

        if (integrationStatus === 'hubspot_success') {
            setStatusMessage({ type: 'success', text: 'Successfully linked HubSpot CRM!' });
            setIsHubspotConnected(true);
            cleanUpQueryParams();
        } else if (authError === 'hubspot_declined') {
            setStatusMessage({ type: 'error', text: 'HubSpot integration connection declined or canceled.' });
            cleanUpQueryParams();
        }
    }, [location.search, cleanUpQueryParams]);

    const handleHubSpotConnect = () => {
        const token = localStorage.getItem('token');
        if (!token) return;
        window.location.href = `${API_URL}/api/auth/hubspot/connect?token=${encodeURIComponent(token)}`;
    };

    const handleHubSpotDisconnect = async () => {
        if (!window.confirm('Are you sure you want to disconnect HubSpot? Automatic lead syncing will stop.')) {
            return;
        }

        setDisconnecting(true);
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/api/auth/hubspot/disconnect`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setIsHubspotConnected(false);
            setStatusMessage({ type: 'success', text: 'HubSpot has been disconnected.' });
        } catch (error) {
            console.error('Error disconnecting HubSpot:', error);
            setStatusMessage({ type: 'error', text: 'Failed to disconnect HubSpot. Please try again.' });
        } finally {
            setDisconnecting(false);
        }
    };

    const isEligibleForHubspot = ['pro', 'business'].includes(userTier.toLowerCase());

    const renderContent = () => {
        switch (activeTab) {
            case 'integrations':
                return (
                    <div className="integrations-tab">
                        <h3>Connected Applications</h3>
                        <p className="tab-description">Link external CRM platforms to automatically sync incoming lead profiles from your cards.</p>

                        {statusMessage.text && (
                            <div className={`status-banner ${statusMessage.type}`}>
                                {statusMessage.text}
                            </div>
                        )}

                        <div className="integration-card hubspot">
                            <div className="integration-info">
                                <div className="hubspot-icon-placeholder">🍁</div>
                                <div>
                                    <h4>
                                        HubSpot CRM
                                        {!isEligibleForHubspot && !loadingTier && (
                                            <span className="pro-badge" style={{ marginLeft: '8px', fontSize: '12px', background: '#ffc107', color: '#000', padding: '2px 6px', borderRadius: '4px' }}>
                                                Pro & Business Only
                                            </span>
                                        )}
                                        {isHubspotConnected && (
                                            <span className="connected-badge" style={{ marginLeft: '8px', fontSize: '12px', background: '#28a745', color: '#fff', padding: '2px 6px', borderRadius: '4px' }}>
                                                Connected
                                            </span>
                                        )}
                                    </h4>
                                    <p>Sync new client interactions, telephone contacts, and email leads seamlessly.</p>
                                </div>
                            </div>

                            {loadingTier ? (
                                <button className="connect-btn hubspot-btn" disabled>Loading...</button>
                            ) : !isEligibleForHubspot ? (
                                <button className="connect-btn hubspot-btn disabled" disabled title="Upgrade to Professional or Business to unlock HubSpot integration">
                                    Upgrade to Connect
                                </button>
                            ) : isHubspotConnected ? (
                                <button 
                                    className="connect-btn hubspot-btn disconnect-btn" 
                                    onClick={handleHubSpotDisconnect}
                                    disabled={disconnecting}
                                    style={{ backgroundColor: '#dc3545', color: '#fff' }}
                                >
                                    {disconnecting ? 'Disconnecting...' : 'Disconnect HubSpot'}
                                </button>
                            ) : (
                                <button 
                                    className="connect-btn hubspot-btn" 
                                    onClick={handleHubSpotConnect}
                                >
                                    Connect HubSpot
                                </button>
                            )}
                        </div>
                    </div>
                );
            case 'account':
                return <Account />;
            case 'subscription':
                return <Subscriptions />;
            case 'branding':
                return <p>Branding: Coming soon</p>;
            default:
                return null;
        }
    };

    return (
        <div className="settings-page">
            <div className="settings-tabs">
                <button className={activeTab === 'integrations' ? 'active' : ''} onClick={() => setActiveTab('integrations')}>Integrations</button>
                <button className={activeTab === 'account' ? 'active' : ''} onClick={() => setActiveTab('account')}>Account</button>
                <button className={activeTab === 'subscription' ? 'active' : ''} onClick={() => setActiveTab('subscription')}>Subscription</button>
                <button className={activeTab === 'branding' ? 'active' : ''} onClick={() => setActiveTab('branding')}>Branding</button>
            </div>
            <div className="settings-content">
                {renderContent()}
            </div>
        </div>
    );
};

export default Settings;